import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LayoutGrid, List as ListIcon } from "lucide-react";
import { http } from "../../lib/http";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader, PageBody } from "../../components/shell/PageChrome";
import ExportMenu from "../../components/ExportMenu";
import { PageToolbar, PrimaryAddButton, DataTable } from "../../components/data/DataTable";
import ViewDrawer from "../../components/data/ViewDrawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import EnrollmentWizard from "../enrollment/EnrollmentWizard";

const STAGES = ["new", "contacted", "trial_scheduled", "trial_done", "negotiating", "enrolled", "lost"];
const STAGE_COLORS = {
  new: "#94a3b8", contacted: "#0A1F44", trial_scheduled: "#0ea5e9",
  trial_done: "#10b981", negotiating: "#f59e0b", enrolled: "#16a34a", lost: "#C8102E",
};
const CEFR = ["pre_a1", "a1", "a1_plus", "a2", "a2_plus", "b1", "b1_plus", "b2", "b2_plus", "c1", "c1_plus", "c2"];

export default function LeadsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = ["admin", "receptionist"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [view, setView] = useState("kanban");
  const [edit, setEdit] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [converting, setConverting] = useState(null);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");

  const fetchList = useCallback(async () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (stageFilter !== "all") p.set("stage", stageFilter);
    const r = await http.get(`/leads?${p}`);
    setItems(r.data.items);
  }, [q, stageFilter]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const save = async (data, id) => {
    try {
      if (id) await http.patch(`/leads/${id}`, data);
      else await http.post(`/leads`, data);
      toast.success(t("leads.saved"));
      setEdit(null); fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const moveStage = async (leadId, newStage) => {
    try {
      await http.patch(`/leads/${leadId}`, { stage: newStage });
      toast.success(t("leads.stageMoved", { stage: t(`leads.stage.${newStage}`) }));
      fetchList();
    } catch (e) { toast.error("Move failed"); }
  };

  return (
    <div data-testid="leads-page">
      <PageHeader title={t("leads.title")} subtitle={t("leads.subtitle")} />
      <PageBody>
        <div className="flex justify-end mb-3"><ExportMenu resource="leads" /></div>
        <PageToolbar
          testid="leads-toolbar"
          search={q} onSearch={setQ}
          filters={(
            <>
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-[160px] bg-white" data-testid="leads-stage-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  {STAGES.map((s) => <SelectItem key={s} value={s}>{t(`leads.stage.${s}`)}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex border border-slate-200 rounded-lg overflow-hidden bg-white">
                <button onClick={() => setView("kanban")} className={`px-3 py-1.5 ${view === "kanban" ? "bg-[#0A1F44] text-white" : "text-slate-600"}`} data-testid="leads-view-kanban"><LayoutGrid size={14} /></button>
                <button onClick={() => setView("list")} className={`px-3 py-1.5 ${view === "list" ? "bg-[#0A1F44] text-white" : "text-slate-600"}`} data-testid="leads-view-list"><ListIcon size={14} /></button>
              </div>
            </>
          )}
          primaryAction={canEdit && <PrimaryAddButton label={t("leads.addLead")} onClick={() => setEdit(emptyLead())} testid="leads-add-button" />}
        />
        {view === "kanban" ? (
          <Kanban items={items} onMove={moveStage} onCardClick={async (l) => setDrawer(l)} onConvert={(l) => setConverting(l)} onEdit={(l) => setEdit(l)} canEdit={canEdit} />
        ) : (
          <DataTable
            testid="leads-table"
            columns={[
              { key: "full_name", label: t("leads.name"), render: (r) => <span className="font-medium text-[#0A1F44]">{r.full_name}</span> },
              { key: "stage", label: t("leads.stage_"), render: (r) => <Badge style={{ backgroundColor: STAGE_COLORS[r.stage], color: "white" }}>{t(`leads.stage.${r.stage}`)}</Badge> },
              { key: "age_group", label: t("leads.ageGroup"), render: (r) => t(`leads.age.${r.age_group}`) },
              { key: "interested_level", label: t("leads.interestedLevel"), render: (r) => (r.interested_level || "").toUpperCase() },
              { key: "phone", label: t("leads.phone") },
              { key: "next", label: t("leads.nextFollowUp"), render: (r) => r.next_follow_up || "—" },
            ]}
            rows={items}
            onRowDoubleClick={(r) => canEdit ? setEdit(r) : setDrawer(r)}
            emptyText={t("leads.empty")}
          />
        )}
      </PageBody>

      <ViewDrawer open={!!drawer} onClose={() => setDrawer(null)} title={drawer?.full_name || ""} testid="lead-drawer">
        {drawer && <LeadDetail lead={drawer} onConvert={() => { setDrawer(null); setConverting(drawer); }} onEdit={() => { setDrawer(null); setEdit(drawer); }} canEdit={canEdit} />}
      </ViewDrawer>

      <LeadEditModal open={!!edit} initial={edit} onCancel={() => setEdit(null)} onSave={(d) => save(d, edit?.id)} />

      <EnrollmentWizard
        open={!!converting}
        leadPrefill={converting}
        onClose={() => setConverting(null)}
        onCommitted={() => { setConverting(null); fetchList(); }}
      />
    </div>
  );
}

function emptyLead() {
  return { full_name: "", age_group: "adult", phone: "", email: "", interested_level: "a1", source: "walk_in", stage: "new", notes: "" };
}

function Kanban({ items, onMove, onCardClick, onConvert, onEdit, canEdit }) {
  const { t } = useTranslation();
  const grouped = STAGES.reduce((acc, s) => ({ ...acc, [s]: items.filter((it) => it.stage === s) }), {});
  const onDrop = (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("lead-id");
    const from = e.dataTransfer.getData("lead-stage");
    if (id && from !== stage) onMove(id, stage);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="leads-kanban">
      {STAGES.map((s) => (
        <div key={s} className="bg-slate-50 rounded-xl p-3 min-h-[300px]" onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, s)} data-testid={`leads-column-${s}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STAGE_COLORS[s] }} />
              <span className="text-xs uppercase tracking-wider text-slate-600 font-medium">{t(`leads.stage.${s}`)}</span>
            </div>
            <Badge variant="outline">{grouped[s].length}</Badge>
          </div>
          <div className="space-y-2">
            {grouped[s].length === 0 && <div className="text-xs text-slate-400 text-center py-4">—</div>}
            {grouped[s].map((l) => (
              <div
                key={l.id}
                draggable={canEdit}
                onDragStart={(e) => { e.dataTransfer.setData("lead-id", l.id); e.dataTransfer.setData("lead-stage", s); }}
                onClick={() => onCardClick(l)}
                onDoubleClick={() => canEdit && onEdit(l)}
                className="bg-white border border-slate-200 rounded-lg p-3 cursor-pointer hover:shadow-md hover:border-slate-300 transition"
                data-testid={`lead-card-${l.id}`}
              >
                <div className="font-medium text-[#0A1F44] text-sm">{l.full_name}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  <Badge variant="outline" className="text-[10px]">{t(`leads.age.${l.age_group}`)}</Badge>
                  <Badge variant="outline" className="text-[10px] uppercase">{l.interested_level}</Badge>
                </div>
                {l.next_follow_up && <div className="text-[11px] text-slate-500 mt-1.5">→ {l.next_follow_up}</div>}
                {canEdit && s !== "enrolled" && s !== "lost" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onConvert(l); }}
                    className="mt-2 text-[10px] uppercase tracking-wider text-[#C8102E] font-medium hover:underline"
                    data-testid={`lead-convert-${l.id}`}
                  >
                    {t("leads.convert")}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadDetail({ lead, onConvert, onEdit, canEdit }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 text-sm" data-testid="lead-detail">
      <div className="grid grid-cols-2 gap-2">
        <span className="text-slate-500">{t("leads.stage_")}</span><Badge style={{ backgroundColor: STAGE_COLORS[lead.stage], color: "white" }} className="w-fit">{t(`leads.stage.${lead.stage}`)}</Badge>
        <span className="text-slate-500">{t("leads.ageGroup")}</span><span>{t(`leads.age.${lead.age_group}`)}</span>
        <span className="text-slate-500">{t("leads.interestedLevel")}</span><span>{(lead.interested_level || "").toUpperCase()}</span>
        <span className="text-slate-500">{t("leads.phone")}</span><span>{lead.phone || "—"}</span>
        <span className="text-slate-500">{t("leads.email")}</span><span>{lead.email || "—"}</span>
      </div>
      {canEdit && (
        <div className="flex gap-2">
          {lead.stage !== "enrolled" && <Button onClick={onConvert} className="bg-[#C8102E] text-white" data-testid="lead-convert-drawer">{t("leads.convert")}</Button>}
          <Button variant="outline" onClick={onEdit}>{t("common.edit")}</Button>
        </div>
      )}
      <div>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">{t("leads.history")}</h3>
        <ul className="space-y-1.5">
          {(lead.history || []).map((h, i) => (
            <li key={i} className="border-l-2 border-slate-200 pl-3 py-1">
              <div className="text-[#0A1F44]">{h.action} {h.from_stage && `(${h.from_stage} → ${h.to_stage})`}</div>
              <div className="text-xs text-slate-400">{h.ts}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function LeadEditModal({ open, initial, onCancel, onSave }) {
  const { t } = useTranslation();
  const [d, setD] = useState(initial || emptyLead());
  useEffect(() => { setD(initial || emptyLead()); }, [initial]);
  if (!open) return null;
  const u = (k, v) => setD({ ...d, [k]: v });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl" data-testid="lead-edit-modal">
        <DialogHeader><DialogTitle>{initial?.id ? t("leads.edit") : t("leads.createTitle")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label={t("leads.name")} required><Input value={d.full_name} onChange={(e) => u("full_name", e.target.value)} data-testid="lead-name-input" /></Field>
          <Field label={t("leads.ageGroup")}>
            <Select value={d.age_group} onValueChange={(v) => u("age_group", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="child">Child</SelectItem><SelectItem value="teen">Teen</SelectItem><SelectItem value="adult">Adult</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("leads.phone")}><Input value={d.phone || ""} onChange={(e) => u("phone", e.target.value)} /></Field>
          <Field label={t("leads.email")}><Input value={d.email || ""} onChange={(e) => u("email", e.target.value)} /></Field>
          <Field label={t("leads.interestedLevel")}>
            <Select value={d.interested_level} onValueChange={(v) => u("interested_level", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CEFR.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("leads.stage_")}>
            <Select value={d.stage} onValueChange={(v) => u("stage", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{t(`leads.stage.${s}`)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t("common.cancel")}</Button>
          <Button className="bg-[#0A1F44] text-white" onClick={() => onSave(d)} data-testid="lead-save-btn">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}{required ? <span className="text-[#C8102E]"> *</span> : null}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
